import React, { useCallback, useEffect, useState } from 'react';
import './AIGateway.css';
import { Icon, Drawer, CodeToken, Select, SelectItem, SelectGroup, SelectSeparator } from '../components/ui';
import {
  getDlpPacks,
  savePackSettings,
  downloadPack,
  importPack,
  deletePack,
  testDlp,
  type DlpPackInfo,
  type DlpTestResult,
  type PackSettings,
} from '../lib/dlpPacksApi';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface AIGatewayProps {
  apiUrl: string;
}

type BreachAction = 'warn' | 'downgrade' | 'block';

interface UsageSummary {
  tokens: number;
  cost_usd: number;
  requests: number;
}

interface GatewayGroup {
  group_id: string;
  display_name: string;
  primary_model: string;
  downgrade_model: string;
  provider: string | null;
  virtual_key: string | null;
  config: string | null;
  credential_id: string | null;
  budget_tokens: number;
  budget_usd: number;
  breach_action: BreachAction;
  warn_threshold_pct: number;
  enabled: number;
  usage?: UsageSummary;
  budget_pct?: number;
}

interface AgentUsageRow {
  agent_id: string;
  name: string | null;
  role: string | null;
  tokens: number;
  cost_usd: number;
  requests: number;
  last_action: string | null;
  budget_tokens: number;
  budget_usd: number;
  breach_action: BreachAction | null;
  enabled: number;
  budget_pct: number;
  primary_model: string | null;
  downgrade_model: string | null;
  group_primary_model: string | null;
  group_downgrade_model: string | null;
  spx_columnar_enabled: number;
}

type DlpCategory = 'SSN' | 'EMAIL' | 'CREDIT_CARD' | 'PHONE' | 'API_KEY' | 'AWS_KEY' | 'PASSWORD';

interface CustomPattern {
  id: string;
  name: string;
  regex: string;
  severity?: 'low' | 'med' | 'high';
  enabled: number;
}

interface DlpSettings {
  enabled: number;
  mode: 'redact' | 'block';
  categories: DlpCategory[];
  custom_patterns: CustomPattern[];
  active_packs?: string[];
  disabled_rules?: string[];
  inspect_request: number;
  inspect_response: number;
  tokenize: number;
  injection_enabled: number;
  injection_block: number;
  scan_images?: number;
  scan_qr?: number;
  image_action_on_hit?: 'block_request' | 'redact_image';
  image_timeout_ms?: number;
  image_max_per_request?: number;
  proxy_passthrough_private?: number;
  proxy_passthrough?: string[];
  proxy_upstream_insecure?: number;
  proxy_allow_pause?: number;
  proxy_pause_max_minutes?: number;
}

interface ThreatEvent {
  id: number;
  timestamp: string;
  agent_id: string;
  kind: string;
  detail: string;
  model: string;
}

interface AgentRisk {
  agent_id: string;
  risk: number;
  events: number;
  breakdown: Record<string, number>;
}

interface ProviderInfo {
  id: string;
  label: string;
  kind: 'cloud' | 'self-hosted';
  base_url?: string;
  enabled: boolean;
}

interface ModelInfo {
  id: string;
  label: string;
  provider: string;
  in_rate: number;
  out_rate: number;
  enabled: boolean;
  pricing_not_set?: boolean;
  status?: 'approved' | 'pending';
}

interface ModelDiscovery {
  refreshed_at: string | null;
  providers: Record<string, { source: 'live' | 'static'; count: number; error?: string }>;
}

interface SIEMEventType {
  event_type: string;
  enabled: boolean;
  description: string;
}

interface SIEMConfig {
  config_id?: string;
  org_id?: string;
  enabled: boolean;
  event_types: string[];
  exclude_event_types: string[];
  kvp_endpoint?: string;
  kvp_enabled: boolean;
  webhook_endpoint?: string;
  webhook_enabled: boolean;
  slack_webhook?: string;
  slack_enabled: boolean;
  teams_webhook?: string;
  teams_enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

// Labels are translation keys, resolved with t() where they are rendered.
const DLP_CATALOG: { id: DlpCategory; labelKey: string; hint: string }[] = [
  { id: 'SSN', labelKey: 'pages.aIGateway.dlpSsn', hint: '123-45-6789' },
  { id: 'CREDIT_CARD', labelKey: 'pages.aIGateway.dlpCreditCard', hint: '4111 1111 1111 1111' },
  { id: 'API_KEY', labelKey: 'pages.aIGateway.dlpApiKeys', hint: 'sk-… / api_key=…' },
  { id: 'AWS_KEY', labelKey: 'pages.aIGateway.dlpAwsKey', hint: 'AKIA…' },
  { id: 'PASSWORD', labelKey: 'pages.aIGateway.dlpPasswords', hint: 'password: …' },
  { id: 'EMAIL', labelKey: 'pages.aIGateway.dlpEmail', hint: 'user@corp.com' },
  { id: 'PHONE', labelKey: 'pages.aIGateway.dlpPhone', hint: '(555) 123-4567' },
];

const ACTION_BADGE: Record<BreachAction, { labelKey: string; cls: string }> = {
  warn: { labelKey: 'pages.aIGateway.actionWarnOnly', cls: 'warn' },
  downgrade: { labelKey: 'pages.aIGateway.actionAutoSwitchModel', cls: 'downgrade' },
  block: { labelKey: 'pages.aIGateway.actionBlockRequests', cls: 'block' },
};

const ROUTING_ACTION_BADGE: Record<string, { labelKey: string; cls: string }> = {
  normal: { labelKey: 'pages.aIGateway.routingNormal', cls: 'downgrade' },
  warn: { labelKey: 'pages.aIGateway.routingWarned', cls: 'warn' },
  downgraded: { labelKey: 'pages.aIGateway.routingDowngraded', cls: 'warn' },
  blocked: { labelKey: 'pages.aIGateway.routingBlocked', cls: 'block' },
};

const THREAT_LABEL: Record<string, { labelKey: string; cls: string }> = {
  prompt_injection: { labelKey: 'pages.aIGateway.threatPromptInjection', cls: 'block' },
  dlp_blocked: { labelKey: 'pages.aIGateway.threatPiiBlocked', cls: 'block' },
  dlp_redacted: { labelKey: 'pages.aIGateway.threatPiiRedacted', cls: 'warn' },
  dlp_tokenized: { labelKey: 'pages.aIGateway.threatPiiTokenized', cls: 'downgrade' },
  budget_blocked: { labelKey: 'pages.aIGateway.threatBudgetBlocked', cls: 'warn' },
  policy_denied: { labelKey: 'pages.aIGateway.threatPolicyDenied', cls: 'block' },
};

const riskBand = (r: number) => (r >= 60 ? 'over' : r >= 30 ? 'near' : 'ok');
const fmt = (n: number) => n.toLocaleString();
const usd = (n: number) => `$${n.toFixed(2)}`;

type Tab = 'routing' | 'providers' | 'budgets' | 'agents' | 'dlp' | 'threats' | 'siem';

interface ProviderCredential {
  credential_id: string;
  org_id: string;
  provider: string;
  label: string;
  department: string | null;
  key_masked: string | null;
  has_key: boolean;
  virtual_key: string | null;
  base_url: string | null;
  data_retention: DataRetention | null;
  trains_on_data: boolean | null;
  is_default: boolean;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/** Provider data-use terms; enforced on fine-tuning / file uploads by the training-data firewall. */
type DataRetention = 'zero' | 'limited' | 'standard' | 'unknown';

/** Draft for the add/edit credential drawer. api_key is write-only (never read back). */
interface CredentialDraft {
  credential_id?: string;
  provider: string;
  label: string;
  department: string;
  api_key: string;
  virtual_key: string;
  base_url: string;
  data_retention: DataRetention;
  /** 'yes' | 'no' | '' (not attested). */
  trains_on_data: '' | 'yes' | 'no';
  is_default: boolean;
  enabled: boolean;
}

/* ---------- ModelSelect ---------- */
/* Newly discovered models arrive disabled; an admin approves them here. */
const PendingModelsPanel: React.FC<{
  models: ModelInfo[];
  providers: ProviderInfo[];
  onSetEnabled: (provider: string, id: string, enabled: boolean) => void;
}> = ({ models, providers, onSetEnabled }) => {
  const { t } = useTranslation();
  const pending = models.filter((m) => m.status === 'pending' && !m.enabled);
  if (pending.length === 0) return null;
  const label = (id: string) => providers.find((p) => p.id === id)?.label || id;
  return (
    <div className="gw-card" style={{ marginBottom: 16 }}>
      <strong>{t('pages.aIGateway.modelsAwaitingApproval', { defaultValue: 'New models awaiting approval' })}</strong>
      <div className="gw-sub" style={{ marginBottom: 12 }}>
        {t('pages.aIGateway.modelsAwaitingApprovalHelp', {
          defaultValue: 'Discovered from your providers. They cannot be selected or routed until you enable them.',
        })}
      </div>
      {pending.map((m) => (
        <div key={`${m.provider}/${m.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
          <span style={{ flex: 1 }}>
            {m.label} <span className="gw-sub">({label(m.provider)} · {m.id})</span>{' '}
            <span className="gw-badge warn">{t('pages.aIGateway.pendingBadge', { defaultValue: 'Pending' })}</span>
            {m.pricing_not_set && (
              <span className="gw-sub"> · {t('pages.aIGateway.noPriceSet', { defaultValue: 'no price set, default rate applies' })}</span>
            )}
          </span>
          <button type="button" className="ent-btn" onClick={() => onSetEnabled(m.provider, m.id, true)}>
            {t('pages.aIGateway.enableModel', { defaultValue: 'Enable' })}
          </button>
        </div>
      ))}
    </div>
  );
};

const ModelSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  providers: ProviderInfo[];
  models: ModelInfo[];
  placeholder?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
  className?: string;
}> = ({ value, onChange, providers, models, placeholder, allowEmpty, emptyLabel, className }) => {
  const { t } = useTranslation();
  const enabledProviders = providers.filter((p) => p.enabled);
  const enabledModels = models.filter((m) => m.enabled);

  return (
    <Select
      value={value}
      onValueChange={onChange}
      placeholder={allowEmpty ? (emptyLabel ?? t('pages.aIGateway.inheritFromGroup')) : (placeholder ?? t('pages.aIGateway.selectModel'))}
      className={className}
    >
      {allowEmpty && (
        <SelectItem value="">{emptyLabel ?? t('pages.aIGateway.inheritFromGroup')}</SelectItem>
      )}
      {allowEmpty && enabledProviders.length > 0 && <SelectSeparator />}
      {enabledProviders.map((p) => {
        const pm = enabledModels.filter((m) => m.provider === p.id);
        if (pm.length === 0) return null;
        return (
          <SelectGroup key={p.id} label={p.label}>
            {pm.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.label}
              </SelectItem>
            ))}
          </SelectGroup>
        );
      })}
      {/* Preserve any model string already saved that isn't in the catalog */}
      {value && !enabledModels.find((m) => m.id === value) && (
        <SelectGroup label={t('pages.aIGateway.custom')}>
          <SelectItem value={value}>{value}</SelectItem>
        </SelectGroup>
      )}
    </Select>
  );
};

/* ---------- Main component ---------- */
const AIGateway: React.FC<AIGatewayProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('routing');
  const [groups, setGroups] = useState<GatewayGroup[]>([]);
  const [dlp, setDlp] = useState<DlpSettings | null>(null);
  const [threats, setThreats] = useState<{ events: ThreatEvent[]; agents: AgentRisk[] }>({ events: [], agents: [] });
  const [agents, setAgents] = useState<AgentUsageRow[]>([]);
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [discovery, setDiscovery] = useState<ModelDiscovery | null>(null);
  const [refreshingModels, setRefreshingModels] = useState(false);
  const [siemConfig, setSiemConfig] = useState<SIEMConfig | null>(null);
  const [siemEventTypes, setSiemEventTypes] = useState<SIEMEventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<GatewayGroup | null>(null);
  const [editingAgent, setEditingAgent] = useState<AgentUsageRow | null>(null);
  const [credentials, setCredentials] = useState<ProviderCredential[]>([]);
  const [editingCred, setEditingCred] = useState<CredentialDraft | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${apiUrl}/admin/gateway/groups`);
      if (!res.ok) throw new Error('Failed to load gateway groups');
      const data = await res.json();
      setGroups(data.groups || []);
    } catch (e: any) {
      setError(e.message || 'Error loading gateway config. Is the firewall running?');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  const fetchModels = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/gateway/models`);
      if (res.ok) {
        const data = await res.json();
        setProviders(data.providers || []);
        setModels(data.models || []);
        setDiscovery(data.discovery || null);
      }
    } catch {
      /* non-fatal */
    }
  }, [apiUrl]);

  const setModelEnabled = useCallback(async (provider: string, id: string, enabled: boolean) => {
    try {
      const res = await fetch(`${apiUrl}/admin/gateway/models/${encodeURIComponent(provider)}/${encodeURIComponent(id)}/enabled`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (res.ok) {
        const data = await res.json();
        setProviders(data.providers || []);
        setModels(data.models || []);
        setDiscovery(data.discovery || null);
      }
    } catch {
      /* non-fatal */
    }
  }, [apiUrl]);

  const refreshModels = useCallback(async () => {
    setRefreshingModels(true);
    try {
      const res = await fetch(`${apiUrl}/admin/gateway/models/refresh`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setProviders(data.providers || []);
        setModels(data.models || []);
        setDiscovery(data.discovery || null);
      }
    } catch {
      /* non-fatal */
    } finally {
      setRefreshingModels(false);
    }
  }, [apiUrl]);

  const fetchDlp = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/gateway/dlp`);
      if (res.ok) {
        const data = await res.json();
        setDlp(data.dlp);
      }
    } catch {
      /* non-fatal */
    }
  }, [apiUrl]);

  const fetchThreats = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/gateway/threats`);
      if (res.ok) setThreats(await res.json());
    } catch {
      /* non-fatal */
    }
  }, [apiUrl]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/gateway/usage/agents`);
      if (res.ok) {
        const data = await res.json();
        setAgents(data.agents || []);
      }
    } catch {
      /* non-fatal */
    }
  }, [apiUrl]);

  const fetchCredentials = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/providers/credentials`);
      if (res.ok) {
        const data = await res.json();
        setCredentials(data.credentials || []);
      }
    } catch {
      /* non-fatal */
    }
  }, [apiUrl]);

  const fetchSiemConfig = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/siem/config`);
      if (res.ok) {
        const data = await res.json();
        setSiemConfig(data);
      }
    } catch {
      /* non-fatal */
    }
  }, [apiUrl]);

  const fetchSiemEventTypes = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/admin/siem/event-types`);
      if (res.ok) {
        const data = await res.json();
        setSiemEventTypes(data);
      }
    } catch {
      /* non-fatal */
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchGroups();
    fetchDlp();
    fetchModels();
    fetchCredentials();
    fetchSiemConfig();
    fetchSiemEventTypes();
  }, [fetchGroups, fetchDlp, fetchModels, fetchCredentials, fetchSiemConfig, fetchSiemEventTypes]);

  useEffect(() => {
    if (tab === 'budgets') {
      const id = setInterval(fetchGroups, 4000);
      return () => clearInterval(id);
    }
    if (tab === 'threats') {
      fetchThreats();
      const id = setInterval(fetchThreats, 4000);
      return () => clearInterval(id);
    }
    if (tab === 'agents') {
      fetchAgents();
      const id = setInterval(fetchAgents, 4000);
      return () => clearInterval(id);
    }
    if (tab === 'siem') {
      fetchSiemConfig();
    }
  }, [tab, fetchGroups, fetchThreats, fetchAgents, fetchSiemConfig]);

  const saveGroup = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await fetch(`${apiUrl}/admin/gateway/groups/${editing.group_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editing),
      });
      if (!res.ok) throw new Error('Save failed');
      setEditing(null);
      await fetchGroups();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveAgentBudget = async () => {
    if (!editingAgent) return;
    setSaving(true);
    try {
      const res = await fetch(`${apiUrl}/admin/gateway/agents/${encodeURIComponent(editingAgent.agent_id)}/budget`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          budget_tokens: editingAgent.budget_tokens,
          budget_usd: editingAgent.budget_usd,
          breach_action: editingAgent.breach_action || 'downgrade',
          enabled: editingAgent.enabled,
          primary_model: editingAgent.primary_model || null,
          downgrade_model: editingAgent.downgrade_model || null,
          spx_columnar_enabled: editingAgent.spx_columnar_enabled ? 1 : 0,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      setEditingAgent(null);
      await fetchAgents();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveCredential = async () => {
    if (!editingCred) return;
    if (!editingCred.provider || !editingCred.label) {
      setError('Provider and label are required');
      return;
    }
    setSaving(true);
    try {
      const isEdit = Boolean(editingCred.credential_id);
      const url = isEdit
        ? `${apiUrl}/admin/providers/credentials/${editingCred.credential_id}`
        : `${apiUrl}/admin/providers/credentials`;
      // Omit api_key on edit when left blank so the stored secret is preserved.
      const payload: any = {
        provider: editingCred.provider,
        label: editingCred.label,
        department: editingCred.department || null,
        virtual_key: editingCred.virtual_key || null,
        base_url: editingCred.base_url || null,
        data_retention: editingCred.data_retention,
        trains_on_data: editingCred.trains_on_data === '' ? null : editingCred.trains_on_data === 'yes',
        is_default: editingCred.is_default,
        enabled: editingCred.enabled,
      };
      if (editingCred.api_key) payload.api_key = editingCred.api_key;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || d.error || 'Save failed');
      }
      setEditingCred(null);
      await fetchCredentials();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const setCredentialDefault = async (id: string) => {
    try {
      await fetch(`${apiUrl}/admin/providers/credentials/${id}/default`, { method: 'POST' });
      await fetchCredentials();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const deleteCredential = async (id: string) => {
    try {
      await fetch(`${apiUrl}/admin/providers/credentials/${id}`, { method: 'DELETE' });
      await fetchCredentials();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const saveDlp = async (next: DlpSettings) => {
    setDlp(next);
    try {
      await fetch(`${apiUrl}/admin/gateway/dlp`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
    } catch {
      /* keep optimistic state */
    }
  };

  const resetUsage = async () => {
    try {
      await fetch(`${apiUrl}/admin/gateway/usage/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      await fetchGroups();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const totals = groups.reduce(
    (acc, g) => ({
      tokens: acc.tokens + (g.usage?.tokens || 0),
      cost: acc.cost + (g.usage?.cost_usd || 0),
      requests: acc.requests + (g.usage?.requests || 0),
    }),
    { tokens: 0, cost: 0, requests: 0 }
  );

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>
            <Icon name="bolt" size={20} /> {t('pages.aIGateway.aiGateway')}
          </h1>
          <p className="subtitle">
            {t('pages.aIGateway.governWhatYourAgentsSendTo')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="ent-btn" onClick={resetUsage} title={t('pages.aIGateway.clearThisMonthsUsageCountersDemo')}>
            <Icon name="refresh" /> {t('pages.aIGateway.resetUsage')}
          </button>
        </div>
      </div>

      {error && (
        <div className="notice error">
          <Icon name="warning" /> {error}
        </div>
      )}

      <div className="gw-tabs">
        <button className={tab === 'routing' ? 'active' : ''} onClick={() => setTab('routing')}>
          <Icon name="chip" /> {t('pages.aIGateway.modelRoutingAutoSwitch')}
        </button>
        <button className={tab === 'providers' ? 'active' : ''} onClick={() => setTab('providers')}>
          <Icon name="key" /> {t('pages.aIGateway.providersKeys')}
        </button>
        <button className={tab === 'budgets' ? 'active' : ''} onClick={() => setTab('budgets')}>
          <Icon name="signal" /> {t('pages.aIGateway.tokenBudgets')}
        </button>
        <button className={tab === 'agents' ? 'active' : ''} onClick={() => setTab('agents')}>
          <Icon name="users" /> {t('pages.aIGateway.perAgentUsage')}
        </button>
        <button className={tab === 'dlp' ? 'active' : ''} onClick={() => setTab('dlp')}>
          <Icon name="lock" /> {t('pages.aIGateway.dataProtection')}
        </button>
        <button className={tab === 'threats' ? 'active' : ''} onClick={() => setTab('threats')}>
          <Icon name="shield" /> {t('pages.aIGateway.threatsTrust')}
        </button>
        <button className={tab === 'siem' ? 'active' : ''} onClick={() => setTab('siem')}>
          <Icon name="bell" /> {t('pages.aIGateway.siemIntegration')}
        </button>
      </div>

      {loading ? (
        <p className="muted-text">{t('pages.aIGateway.loadingGatewayConfiguration')}</p>
      ) : (
        <>
          {tab === 'routing' && (
            <RoutingTab groups={groups} providers={providers} onEdit={setEditing} />
          )}
          {tab === 'providers' && (
            <PendingModelsPanel models={models} providers={providers} onSetEnabled={setModelEnabled} />
          )}
          {tab === 'providers' && (
            <ProvidersTab
              providers={providers}
              credentials={credentials}
              onAdd={(provider) =>
                setEditingCred({
                  provider: provider || '',
                  label: '',
                  department: '',
                  api_key: '',
                  virtual_key: '',
                  base_url: '',
                  data_retention: 'unknown',
                  trains_on_data: '',
                  is_default: false,
                  enabled: true,
                })
              }
              onEdit={(c) =>
                setEditingCred({
                  credential_id: c.credential_id,
                  provider: c.provider,
                  label: c.label,
                  department: c.department || '',
                  api_key: '',
                  virtual_key: c.virtual_key || '',
                  base_url: c.base_url || '',
                  data_retention: c.data_retention || 'unknown',
                  trains_on_data: c.trains_on_data == null ? '' : c.trains_on_data ? 'yes' : 'no',
                  is_default: c.is_default,
                  enabled: c.enabled,
                })
              }
              onSetDefault={setCredentialDefault}
              onDelete={deleteCredential}
            />
          )}
          {tab === 'budgets' && (
            <BudgetsTab groups={groups} totals={totals} />
          )}
          {tab === 'agents' && (
            <AgentsTab agents={agents} onEdit={setEditingAgent} />
          )}
          {tab === 'dlp' && dlp && (
            <DlpTab
              dlp={dlp}
              apiUrl={apiUrl}
              onChange={saveDlp}
              onPackSettings={(change) => setDlp((d) => (d ? { ...d, ...change } : d))}
            />
          )}
          {tab === 'threats' && <ThreatsTab threats={threats} />}
          {tab === 'siem' && siemConfig && (
            <SIEMTab
              config={siemConfig}
              eventTypes={siemEventTypes}
              onSave={async (updated) => {
                const res = await fetch(`${apiUrl}/admin/siem/config`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(updated),
                });
                if (res.ok) {
                  setSiemConfig(await res.json());
                }
              }}
              apiUrl={apiUrl}
            />
          )}
        </>
      )}

      {editing && (
        <Drawer
          title={t('pages.aIGateway.editPolicyTitle', { name: editing.display_name })}
          width={520}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setEditing(null)}>
                {t('pages.aIGateway.cancel')}
              </button>
              <button className="ent-btn primary" onClick={saveGroup} disabled={saving}>
                <Icon name="checkCircle" /> {saving ? t('pages.aIGateway.saving') : t('pages.aIGateway.savePolicy')}
              </button>
            </>
          }
        >
          <GroupForm group={editing} onChange={setEditing} providers={providers} models={models} credentials={credentials} />
        </Drawer>
      )}

      {editingAgent && (
        <Drawer
          title={t('pages.aIGateway.agentBudgetTitle', { name: editingAgent.name || editingAgent.agent_id })}
          width={480}
          onClose={() => setEditingAgent(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setEditingAgent(null)}>
                {t('pages.aIGateway.cancel')}
              </button>
              <button className="ent-btn primary" onClick={saveAgentBudget} disabled={saving}>
                <Icon name="checkCircle" /> {saving ? t('pages.aIGateway.saving') : t('pages.aIGateway.saveBudget')}
              </button>
            </>
          }
        >
          <AgentBudgetForm agent={editingAgent} onChange={setEditingAgent} providers={providers} models={models} discovery={discovery} onRefreshModels={refreshModels} refreshingModels={refreshingModels} />
        </Drawer>
      )}

      {editingCred && (
        <Drawer
          title={editingCred.credential_id ? `Edit key — ${editingCred.label || editingCred.provider}` : t('pages.aIGateway.addProviderKey')}
          width={480}
          onClose={() => setEditingCred(null)}
          footer={
            <>
              <button className="ent-btn" onClick={() => setEditingCred(null)}>
                {t('pages.aIGateway.cancel')}
              </button>
              <button className="ent-btn primary" onClick={saveCredential} disabled={saving}>
                <Icon name="checkCircle" /> {saving ? t('pages.aIGateway.saving') : t('pages.aIGateway.saveKey')}
              </button>
            </>
          }
        >
          <CredentialForm draft={editingCred} onChange={setEditingCred} providers={providers} />
        </Drawer>
      )}
    </div>
  );
};

/* ---------- Routing tab ---------- */
const RoutingTab: React.FC<{
  groups: GatewayGroup[];
  providers: ProviderInfo[];
  onEdit: (g: GatewayGroup) => void;
}> = ({ groups, providers, onEdit }) => {
  const { t } = useTranslation();
  const providerLabel = (id: string | null) => {
    if (!id) return 'default';
    return providers.find((p) => p.id === id)?.label ?? id;
  };

  return (
    <>
      <p className="gw-help">
        {t('pages.aIGateway.eachGroupMapsToAnAgent')} <strong>{t('pages.aIGateway.primaryModel')}</strong>{t('pages.aIGateway.andAutomaticallySwitchesToThe')} <strong>{t('pages.aIGateway.economyModel')}</strong> {t('pages.aIGateway.whenTheTokenBudgetIsExhausted')}
      </p>
      <div className="ent-table-wrap scroll">
        <table className="ent-table">
          <thead>
            <tr>
              <th>{t('pages.aIGateway.group')}</th>
              <th>{t('pages.aIGateway.primaryModel2')}</th>
              <th>{t('pages.aIGateway.economyAutoSwitch')}</th>
              <th>{t('pages.aIGateway.provider')}</th>
              <th>{t('pages.aIGateway.onBudgetBreach')}</th>
              <th style={{ width: 80 }}>{t('pages.aIGateway.status')}</th>
              <th style={{ width: 70, textAlign: 'right' }}>{t('pages.aIGateway.edit')}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.group_id}>
                <td style={{ fontWeight: 600 }}>
                  {g.display_name}
                  <div className="gw-sub">{g.group_id}</div>
                </td>
                <td>
                  <CodeToken>{g.primary_model}</CodeToken>
                </td>
                <td>
                  <CodeToken muted>{g.downgrade_model}</CodeToken>
                </td>
                <td style={{ color: 'var(--text-2)' }}>{providerLabel(g.provider)}</td>
                <td>
                  <span className={`gw-badge ${ACTION_BADGE[g.breach_action].cls}`}>
                    {t(ACTION_BADGE[g.breach_action].labelKey)}
                  </span>
                </td>
                <td>
                  <span className={`gw-dot ${g.enabled ? 'on' : 'off'}`} />
                  {g.enabled ? t('pages.aIGateway.enforced') : t('pages.aIGateway.off')}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button className="icon-btn" onClick={() => onEdit(g)} aria-label={t('pages.aIGateway.edit')}>
                    <Icon name="settings" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

/* ---------- Providers & Keys tab ---------- */
const ProvidersTab: React.FC<{
  providers: ProviderInfo[];
  credentials: ProviderCredential[];
  onAdd: (provider?: string) => void;
  onEdit: (c: ProviderCredential) => void;
  onSetDefault: (id: string) => void;
  onDelete: (id: string) => void;
}> = ({ providers, credentials, onAdd, onEdit, onSetDefault, onDelete }) => {
  const { t } = useTranslation();
  const providerLabel = (id: string) => providers.find((p) => p.id === id)?.label ?? id;
  // Group keys by provider; only show providers that have keys OR are enabled.
  const byProvider = new Map<string, ProviderCredential[]>();
  for (const c of credentials) {
    const arr = byProvider.get(c.provider) || [];
    arr.push(c);
    byProvider.set(c.provider, arr);
  }
  const providerIds = Array.from(
    new Set<string>([...providers.filter((p) => p.enabled).map((p) => p.id), ...byProvider.keys()])
  );

  return (
    <>
      <p className="gw-help">
        {t('pages.aIGateway.registerTheLLMProvidersYouRoute')} <strong>{t('pages.aIGateway.default')}</strong>{t('pages.aIGateway.policiesAndGroupsThatDontPin')}
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="ent-btn primary" onClick={() => onAdd()}>
          <Icon name="plus" /> {t('pages.aIGateway.addKey')}
        </button>
      </div>

      {providerIds.length === 0 && (
        <p className="muted-text">{t('pages.aIGateway.noProvidersEnabledEnableProvidersIn')}</p>
      )}

      {providerIds.map((pid) => {
        const keys = byProvider.get(pid) || [];
        return (
          <div key={pid} className="gw-card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="server" size={16} /> {providerLabel(pid)}
                <span className="muted-text" style={{ fontWeight: 400, fontSize: 12 }}>
                  {t('pages.aIGateway.keyCount', { count: keys.length })}
                </span>
              </h3>
              <button className="ent-btn sm" onClick={() => onAdd(pid)}>
                <Icon name="plus" /> {t('pages.aIGateway.addKey')}
              </button>
            </div>
            {keys.length === 0 ? (
              <p className="muted-text" style={{ margin: 0 }}>
                {t('pages.aIGateway.noKeysYetRequestsForThis')}
              </p>
            ) : (
              <div className="ent-table-wrap">
                <table className="ent-table">
                  <thead>
                    <tr>
                      <th>{t('pages.aIGateway.label')}</th>
                      <th>{t('pages.aIGateway.department')}</th>
                      <th>{t('pages.aIGateway.key')}</th>
                      <th>{t('pages.aIGateway.default2')}</th>
                      <th>{t('pages.aIGateway.status')}</th>
                      <th style={{ textAlign: 'right' }}>{t('pages.aIGateway.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((c) => (
                      <tr key={c.credential_id}>
                        <td>{c.label}</td>
                        <td>{c.department || <span className="muted-text">—</span>}</td>
                        <td>
                          {c.has_key ? <CodeToken>{c.key_masked || '••••'}</CodeToken> : <span className="muted-text">{t('pages.aIGateway.none')}</span>}
                        </td>
                        <td>
                          {c.is_default ? (
                            <span className="gw-pill ok">
                              <Icon name="checkBadge" size={12} /> {t('pages.aIGateway.default')}
                            </span>
                          ) : (
                            <button className="ent-btn sm ghost" onClick={() => onSetDefault(c.credential_id)}>
                              {t('pages.aIGateway.makeDefault')}
                            </button>
                          )}
                        </td>
                        <td>
                          <span className={`gw-pill ${c.enabled ? 'ok' : 'muted'}`}>{c.enabled ? t('pages.aIGateway.enabled') : t('pages.aIGateway.disabled')}</span>
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button className="ent-btn sm" onClick={() => onEdit(c)} title={t('pages.aIGateway.edit')}>
                            <Icon name="settings" />
                          </button>{' '}
                          <button className="ent-btn sm danger" onClick={() => onDelete(c.credential_id)} title={t('pages.aIGateway.delete')}>
                            <Icon name="trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

/* ---------- Credential add/edit form ---------- */
const CredentialForm: React.FC<{
  draft: CredentialDraft;
  onChange: (d: CredentialDraft) => void;
  providers: ProviderInfo[];
}> = ({ draft, onChange, providers }) => {
  const { t } = useTranslation();
  const set = (patch: Partial<CredentialDraft>) => onChange({ ...draft, ...patch });
  const selectedProvider = providers.find((p) => p.id === draft.provider);
  const isSelfHosted = selectedProvider?.kind === 'self-hosted';

  return (
    <div className="gw-form">
      <label className="gw-field">
        <span>{t('pages.aIGateway.provider2')}</span>
        <Select
          value={draft.provider}
          onValueChange={(v) => set({ provider: v })}
          disabled={Boolean(draft.credential_id)}
          placeholder={t('pages.aIGateway.selectProvider')}
        >
          {providers.filter((p) => p.enabled).map((p) => (
            <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
          ))}
        </Select>
      </label>

      <label className="gw-field">
        <span>{t('pages.aIGateway.label2')}</span>
        <input
          className="ent-input"
          value={draft.label}
          onChange={(e) => set({ label: e.target.value })}
          placeholder={t('pages.aIGateway.eGFinanceProductionKey')}
        />
      </label>

      <label className="gw-field">
        <span>{t('pages.aIGateway.department')}</span>
        <input
          className="ent-input"
          value={draft.department}
          onChange={(e) => set({ department: e.target.value })}
          placeholder={t('pages.aIGateway.eGFinanceOptionalAttribution')}
        />
      </label>

      <label className="gw-field">
        <span>{t('pages.aIGateway.apiKey')} {draft.credential_id ? t('pages.aIGateway.leaveBlankToKeep') : ''}</span>
        <input
          className="ent-input"
          type="password"
          autoComplete="new-password"
          value={draft.api_key}
          onChange={(e) => set({ api_key: e.target.value })}
          placeholder={draft.credential_id ? t('pages.aIGateway.unchanged') : t('pages.aIGateway.sk')}
        />
      </label>

      <label className="gw-field">
        <span>{t('pages.aIGateway.portkeyVirtualKeyOptional')}</span>
        <input
          className="ent-input"
          value={draft.virtual_key}
          onChange={(e) => set({ virtual_key: e.target.value })}
          placeholder={t('pages.aIGateway.vkVaultReferenceInsteadOfA')}
        />
      </label>

      {isSelfHosted && (
        <label className="gw-field">
          <span>{t('pages.aIGateway.baseURLSelfHosted')}</span>
          <input
            className="ent-input"
            value={draft.base_url}
            onChange={(e) => set({ base_url: e.target.value })}
            placeholder={t('pages.aIGateway.httpOllama11434V1')}
          />
        </label>
      )}

      <label className="gw-field">
        <span>{t('pages.aIGateway.dataRetentionTrainingDataFirewall')}</span>
        <select
          className="ent-input"
          value={draft.data_retention}
          onChange={(e) => set({ data_retention: e.target.value as DataRetention })}
        >
          <option value="unknown">{t('pages.aIGateway.unknownNotAttested')}</option>
          <option value="zero">{t('pages.aIGateway.zeroDataRetention')}</option>
          <option value="limited">{t('pages.aIGateway.limitedEG30DayAbuse')}</option>
          <option value="standard">{t('pages.aIGateway.standardRetention')}</option>
        </select>
      </label>

      <label className="gw-field">
        <span>{t('pages.aIGateway.providerTrainsOnSubmittedData')}</span>
        <select
          className="ent-input"
          value={draft.trains_on_data}
          onChange={(e) => set({ trains_on_data: e.target.value as CredentialDraft['trains_on_data'] })}
        >
          <option value="">{t('pages.aIGateway.notAttested')}</option>
          <option value="no">{t('pages.aIGateway.no')}</option>
          <option value="yes">{t('pages.aIGateway.yesBlockTrainingUploads')}</option>
        </select>
      </label>

      <label className="gw-check">
        <input type="checkbox" checked={draft.is_default} onChange={(e) => set({ is_default: e.target.checked })} />
        <span>{t('pages.aIGateway.defaultKeyForThisProvider')}</span>
      </label>

      <label className="gw-check">
        <input type="checkbox" checked={draft.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
        <span>{t('pages.aIGateway.enabled2')}</span>
      </label>
    </div>
  );
};

/* ---------- Budgets tab ---------- */
const BudgetsTab: React.FC<{
  groups: GatewayGroup[];
  totals: { tokens: number; cost: number; requests: number };
}> = ({ groups, totals }) => {
  const { t } = useTranslation();
  return (
  <>
    <div className="gw-tiles">
      <div className="gw-tile">
        <span className="gw-tile-label">{t('pages.aIGateway.tokensThisMonth')}</span>
        <span className="gw-tile-value">{fmt(totals.tokens)}</span>
      </div>
      <div className="gw-tile">
        <span className="gw-tile-label">{t('pages.aIGateway.estimatedSpend')}</span>
        <span className="gw-tile-value">{usd(totals.cost)}</span>
      </div>
      <div className="gw-tile">
        <span className="gw-tile-label">{t('pages.aIGateway.requests')}</span>
        <span className="gw-tile-value">{fmt(totals.requests)}</span>
      </div>
    </div>

    <div className="gw-meters">
      {groups.map((g) => {
        const pct = Math.min((g.budget_pct || 0) * 100, 100);
        const over = (g.budget_pct || 0) >= 1;
        const near = !over && pct >= g.warn_threshold_pct;
        const state = over ? 'over' : near ? 'near' : 'ok';
        return (
          <div className={`gw-meter ${state}`} key={g.group_id}>
            <div className="gw-meter-head">
              <div>
                <strong>{g.display_name}</strong>
                <span className="gw-sub"> · {g.primary_model}</span>
              </div>
              <span className={`gw-badge ${ACTION_BADGE[g.breach_action].cls}`}>
                {t(ACTION_BADGE[g.breach_action].labelKey)}
              </span>
            </div>
            <div className="gw-bar">
              <div className="gw-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="gw-meter-foot">
              <span>
                {t('pages.aIGateway.tokenUsage', { used: fmt(g.usage?.tokens || 0), budget: g.budget_tokens > 0 ? fmt(g.budget_tokens) : '∞' })}
                {' · '}
                {usd(g.usage?.cost_usd || 0)}
              </span>
              <span className="gw-pct">
                {over ? (
                  <>
                    <Icon name="warning" size={14} /> {t('pages.aIGateway.overBudget')}
                  </>
                ) : (
                  `${Math.round(pct)}%`
                )}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  </>
);
};

/* ---------- Per-agent usage tab ---------- */
const AgentsTab: React.FC<{ agents: AgentUsageRow[]; onEdit: (a: AgentUsageRow) => void }> = ({ agents, onEdit }) => {
  const { t } = useTranslation();
  return (
  <>
    <p className="gw-help">
      {t('pages.aIGateway.liveTokenSpend')} <strong>{t('pages.aIGateway.perAgent')}</strong>{t('pages.aIGateway.attributedFromEveryProxiedCallGive')}
    </p>
    {agents.length === 0 ? (
      <div className="ent-empty">
        <span className="icon">
          <Icon name="users" />
        </span>
        <h4>{t('pages.aIGateway.noAgentsRegistered')}</h4>
        <p>{t('pages.aIGateway.agentsAppearHereOnceTheyAre')}</p>
      </div>
    ) : (
      <div className="ent-table-wrap scroll">
        <table className="ent-table">
          <thead>
            <tr>
              <th>{t('pages.aIGateway.agent')}</th>
              <th style={{ textAlign: 'right' }}>{t('pages.aIGateway.tokens')}</th>
              <th style={{ textAlign: 'right' }}>{t('pages.aIGateway.est')}</th>
              <th style={{ textAlign: 'right' }}>{t('pages.aIGateway.requests')}</th>
              <th style={{ width: 200 }}>{t('pages.aIGateway.personalBudget')}</th>
              <th style={{ width: 110 }}>{t('pages.aIGateway.lastAction')}</th>
              <th style={{ width: 70, textAlign: 'right' }}>{t('pages.aIGateway.budget')}</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => {
              const hasBudget = a.enabled === 1 && a.budget_tokens > 0;
              const pct = Math.min((a.budget_pct || 0) * 100, 100);
              const over = (a.budget_pct || 0) >= 1;
              const state = over ? 'over' : pct >= 80 ? 'near' : 'ok';
              const action = ROUTING_ACTION_BADGE[a.last_action || 'normal'] || ROUTING_ACTION_BADGE.normal;
              const hasModelOverride = !!(a.primary_model || a.downgrade_model);
              return (
                <tr key={a.agent_id}>
                  <td style={{ fontWeight: 600 }}>
                    {a.name || a.agent_id}
                    <div className="gw-sub">
                      {a.role || t('pages.aIGateway.unknownRole')} · {a.agent_id}
                      {hasModelOverride && (
                        <span className="gw-badge downgrade" style={{ marginLeft: 6, fontSize: 10 }}>
                          {t('pages.aIGateway.modelOverride')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>{fmt(a.tokens)}</td>
                  <td style={{ textAlign: 'right' }}>{usd(a.cost_usd)}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(a.requests)}</td>
                  <td>
                    {hasBudget ? (
                      <div className={`gw-agent-meter ${state}`}>
                        <div className="gw-bar">
                          <div className="gw-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="gw-sub">
                          {fmt(a.tokens)} / {fmt(a.budget_tokens)} · {t(ACTION_BADGE[a.breach_action || 'downgrade'].labelKey)}
                        </div>
                      </div>
                    ) : (
                      <span className="gw-sub">{t('pages.aIGateway.groupBudgetOnly')}</span>
                    )}
                  </td>
                  <td>
                    {a.requests > 0 ? (
                      <span className={`gw-badge ${action.cls}`}>{t(action.labelKey)}</span>
                    ) : (
                      <span className="gw-sub">—</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="icon-btn" onClick={() => onEdit(a)} aria-label={t('pages.aIGateway.setBudget')}>
                      <Icon name="settings" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </>
);
};

/* ---------- DLP tab ---------- */
/* ---------- Custom (user-defined) detectors ---------- */
// Highlight every match of `re` inside `text` for the live tester.
function highlightMatches(text: string, re: RegExp | null): { count: number; nodes: React.ReactNode } {
  if (!re || !text) return { count: 0, nodes: text };
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let count = 0;
  const g = new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g');
  let m: RegExpExecArray | null;
  while ((m = g.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    nodes.push(
      <mark key={`${m.index}-${count}`} className="cp-hit">
        {m[0] || ''}
      </mark>
    );
    last = m.index + (m[0]?.length || 0);
    count++;
    if (m[0] === '') g.lastIndex++; // avoid zero-width infinite loop
  }
  if (last < text.length) nodes.push(text.slice(last));
  return { count, nodes };
}

const SEVERITIES: Array<'low' | 'med' | 'high'> = ['low', 'med', 'high'];

const CustomDetectors: React.FC<{
  patterns: CustomPattern[];
  onChange: (next: CustomPattern[]) => void;
}> = ({ patterns, onChange }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [regex, setRegex] = useState('');
  const [severity, setSeverity] = useState<'low' | 'med' | 'high'>('med');
  const [sample, setSample] = useState('');

  // Compile the draft regex defensively; surface a friendly error if invalid.
  let compiled: RegExp | null = null;
  let regexError = '';
  if (regex) {
    try {
      compiled = new RegExp(regex);
    } catch (e) {
      regexError = (e as Error).message;
    }
  }
  const { count, nodes } = highlightMatches(sample, compiled);

  const canAdd = !!name.trim() && !!regex.trim() && !regexError;

  const add = () => {
    if (!canAdd) return;
    const next: CustomPattern = {
      id: `cp_${Date.now().toString(36)}`,
      name: name.trim(),
      regex: regex.trim(),
      severity,
      enabled: 1,
    };
    onChange([...(patterns || []), next]);
    setName('');
    setRegex('');
    setSeverity('med');
    setSample('');
  };

  const toggle = (id: string) =>
    onChange((patterns || []).map((p) => (p.id === id ? { ...p, enabled: p.enabled ? 0 : 1 } : p)));
  const remove = (id: string) => onChange((patterns || []).filter((p) => p.id !== id));

  return (
    <div className="gw-card">
      <strong>{t('pages.aIGateway.yourOwnRules')}</strong>
      <div className="gw-sub" style={{ marginBottom: 12 }}>
        {t('pages.aIGateway.defineYourOwnSensitiveDataEmployee')}
      </div>

      {(patterns || []).length > 0 && (
        <div className="cp-list">
          {(patterns || []).map((p) => (
            <div key={p.id} className={`cp-row ${p.enabled ? '' : 'off'}`}>
              <label className="cp-enable" title={p.enabled ? t('pages.aIGateway.enabled2') : t('pages.aIGateway.disabled2')}>
                <input type="checkbox" checked={!!p.enabled} onChange={() => toggle(p.id)} />
              </label>
              <div className="cp-meta">
                <span className="cp-name">{p.name}</span>
                <code className="cp-regex">{p.regex}</code>
              </div>
              <span className={`cp-sev sev-${p.severity || 'med'}`}>{p.severity || t('pages.aIGateway.med')}</span>
              <button type="button" className="icon-btn danger" onClick={() => remove(p.id)} title={t('pages.aIGateway.removeDetector')}>
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="cp-editor">
        <div className="cp-editor-grid">
          <div className="gw-field">
            <label>{t('pages.aIGateway.name')}</label>
            <input
              className="ent-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('pages.aIGateway.eGEmployeeID')}
            />
          </div>
          <div className="gw-field">
            <label>{t('pages.aIGateway.severity')}</label>
            <div className="cp-sev-pick">
              {SEVERITIES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`cp-sev sev-${s} ${severity === s ? 'sel' : ''}`}
                  onClick={() => setSeverity(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="gw-field">
          <label>{t('pages.aIGateway.patternRegularExpression')}</label>
          <input
            className={`ent-input cp-regex-input ${regexError ? 'invalid' : ''}`}
            value={regex}
            onChange={(e) => setRegex(e.target.value)}
            placeholder={t('pages.aIGateway.eGBEMPD6B')}
            spellCheck={false}
          />
          {regexError ? (
            <div className="cp-status invalid">
              <Icon name="warning" size={13} /> {t('pages.aIGateway.invalidRegex', { error: regexError })}
            </div>
          ) : regex ? (
            <div className="cp-status valid">
              <Icon name="check" size={13} /> {t('pages.aIGateway.validPattern')}
            </div>
          ) : null}
        </div>

        <div className="gw-field">
          <label>{t('pages.aIGateway.testItPasteSampleText')}</label>
          <textarea
            className="ent-input cp-sample"
            rows={3}
            value={sample}
            onChange={(e) => setSample(e.target.value)}
            placeholder={t('pages.aIGateway.pasteTextContainingTheDataYou')}
          />
          {sample && (
            <div className="cp-preview">
              <div className="cp-preview-head">
                {count > 0 ? (
                  <span className="cp-count hit">{t('pages.aIGateway.matchCount', { count })}</span>
                ) : (
                  <span className="cp-count">{t('pages.aIGateway.noMatches')}</span>
                )}
              </div>
              <div className="cp-preview-body">{nodes}</div>
            </div>
          )}
        </div>

        <button type="button" className="ent-btn primary sm" disabled={!canAdd} onClick={add}>
          <Icon name="plus" size={14} /> {t('pages.aIGateway.addDetector')}
        </button>
      </div>
    </div>
  );
};

const IndustryPacks: React.FC<{
  apiUrl: string;
  onPackSettings: (change: Partial<PackSettings>) => void;
}> = ({ apiUrl, onPackSettings }) => {
  const { t } = useTranslation();
  const [packs, setPacks] = useState<DlpPackInfo[] | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState<{ text: string; warnings: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [result, setResult] = useState<DlpTestResult | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const reload = useCallback(
    () =>
      getDlpPacks(apiUrl)
        .then(setPacks)
        .catch((e) => setError(e.message)),
    [apiUrl]
  );
  useEffect(() => {
    reload();
  }, [reload]);

  /** Run a server change; surface failures instead of silently flipping the UI. */
  const run = async (label: string, fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice(null);
    try {
      await fn();
      setResult(null);
    } catch (e) {
      setError(`${label}: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const disabledKeys = (list: DlpPackInfo[]) =>
    list.flatMap((p) => p.rules.filter((r) => !r.enabled).map((r) => `${p.id}:${r.id}`));

  const togglePack = (id: string) =>
    run('Could not save', async () => {
      if (!packs) return;
      const next = packs.filter((p) => (p.id === id ? !p.active : p.active)).map((p) => p.id);
      const saved = await savePackSettings(apiUrl, { active_packs: next });
      setPacks(packs.map((p) => ({ ...p, active: saved.active_packs.includes(p.id) })));
      onPackSettings(saved);
    });

  const toggleRule = (packId: string, ruleId: string) =>
    run('Could not save', async () => {
      if (!packs) return;
      const key = `${packId}:${ruleId}`;
      const off = new Set(disabledKeys(packs));
      if (off.has(key)) off.delete(key);
      else off.add(key);
      const saved = await savePackSettings(apiUrl, { disabled_rules: Array.from(off) });
      const savedOff = new Set(saved.disabled_rules);
      setPacks(packs.map((p) => ({ ...p, rules: p.rules.map((r) => ({ ...r, enabled: !savedOff.has(`${p.id}:${r.id}`) })) })));
      onPackSettings(saved);
    });

  const onFile = (file: File | undefined) => {
    if (!file) return;
    run('Could not load pack', async () => {
      const r = await importPack(apiUrl, await file.text());
      setNotice({
        text: `${r.replaced ? 'Replaced' : 'Loaded'} pack "${r.pack.name}" (${r.pack.rules} rules). It is not active until you turn it on.`,
        warnings: r.warnings,
      });
      await reload();
    }).finally(() => {
      if (fileRef.current) fileRef.current.value = '';
    });
  };

  const removePack = (p: DlpPackInfo) => {
    if (!window.confirm(`Delete the custom pack "${p.name}"? This cannot be undone.`)) return;
    run('Could not delete', async () => {
      await deletePack(apiUrl, p.id);
      await reload();
      const settings = await savePackSettings(apiUrl, {});
      onPackSettings(settings);
    });
  };

  const runTest = async () => {
    setError('');
    try {
      setResult(await testDlp(apiUrl, text));
    } catch (e) {
      setError(`Test failed: ${(e as Error).message}`);
    }
  };

  const activeCount = packs?.filter((p) => p.active).length ?? 0;

  return (
    <div className="gw-card">
      <strong>{t('pages.aIGateway.industryPacks')}</strong>
      <div className="gw-sub" style={{ marginBottom: 12 }}>
        {t('pages.aIGateway.packsIntro')}
        {packs && ` ${activeCount} of ${packs.length} active.`}
      </div>

      <div className="ip-toolbar">
        <button type="button" className="ent-btn sm" disabled={busy} onClick={() => fileRef.current?.click()}>
          <Icon name="plus" size={14} /> {t('pages.aIGateway.loadPackFile')}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <span className="gw-sub">
          {t('pages.aIGateway.use')} <em>{t('pages.aIGateway.download')}</em> {t('pages.aIGateway.onAnyPackForTheFile')} <code>acme-bank</code>).
        </span>
      </div>

      {error && (
        <div className="cp-status invalid" style={{ marginBottom: 10 }} role="alert">
          <Icon name="warning" size={13} /> {error}
        </div>
      )}
      {notice && (
        <div className="cp-status valid" style={{ marginBottom: 10 }}>
          <Icon name="check" size={13} /> {notice.text}
          {notice.warnings.map((w) => (
            <div key={w} className="gw-sub">{t('pages.aIGateway.note', { v0: w })}</div>
          ))}
        </div>
      )}
      {!packs && !error && <div className="gw-sub">{t('pages.aIGateway.loadingPacks')}</div>}

      <div className="ip-list">
        {(packs || []).map((p) => {
          const enabled = p.rules.filter((r) => r.enabled).length;
          const isOpen = open === p.id;
          return (
            <div key={p.id} className="ip-pack">
              <button
                type="button"
                className={`gw-detector ${p.active ? 'on' : ''}`}
                onClick={() => togglePack(p.id)}
                disabled={busy}
                aria-pressed={p.active}
              >
                <span className="gw-detector-check">{p.active && <Icon name="check" size={14} />}</span>
                <span>
                  <strong>{p.name}</strong>
                  {p.custom && <span className="ip-badge">{t('pages.aIGateway.custom')}</span>}
                  <div className="gw-sub">{p.description}</div>
                </span>
              </button>
              <div className="ip-actions">
                <span className="gw-sub">
                  <strong className={p.active ? 'ip-state on' : 'ip-state'}>{p.active ? t('pages.aIGateway.active') : t('pages.aIGateway.offNotScanning')}</strong>
                  {' · '}
                  {t('pages.aIGateway.rulesEnabledOf', { enabled, total: p.rules.length })}
                </span>
                <button
                  type="button"
                  className="ent-btn sm"
                  onClick={() => setOpen(isOpen ? null : p.id)}
                  aria-expanded={isOpen}
                >
                  {isOpen ? t('pages.aIGateway.hideRules') : t('pages.aIGateway.showRulesRegex')}
                </button>
                <button
                  type="button"
                  className="ent-btn sm"
                  onClick={() => run('Download failed', () => downloadPack(apiUrl, p.id))}
                  disabled={busy}
                >
                  {t('pages.aIGateway.download')}
                </button>
                {p.custom && (
                  <button type="button" className="ent-btn sm" onClick={() => removePack(p)} disabled={busy}>
                    {t('pages.aIGateway.delete')}
                  </button>
                )}
              </div>
              {isOpen && !p.active && (
                <div className="cp-status invalid" role="status">
                  <Icon name="warning" size={13} /> {t('pages.aIGateway.thisPackIsOffSoNone')}
                </div>
              )}
              {isOpen && (
                <div className={`cp-list ${p.active ? '' : 'ip-inactive'}`}>
                  {p.rules.map((r) => (
                    <div key={r.id} className={`cp-row ${r.enabled ? '' : 'off'}`}>
                      <label className="cp-enable" title={r.enabled ? t('pages.aIGateway.ruleEnabled') : t('pages.aIGateway.ruleDisabled')}>
                        <input
                          type="checkbox"
                          checked={r.enabled}
                          disabled={busy}
                          onChange={() => toggleRule(p.id, r.id)}
                          aria-label={t('pages.aIGateway.ruleEnabled', { name: r.name })}
                        />
                      </label>
                      <div className="cp-meta">
                        <span className="cp-name">
                          {r.name}
                          {r.validator ? ` · ${r.validator} checksum` : ''}
                        </span>
                        <code className="ip-regex">{r.regex}</code>
                        {r.sample && <span className="gw-sub">{t('pages.aIGateway.eG', { v0: r.sample })}</span>}
                      </div>
                      <span className={`cp-sev sev-${r.severity}`}>{r.severity}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="gw-field" style={{ marginTop: 12 }}>
        <label>{t('pages.aIGateway.tryItPasteTextToTest')}</label>
        <textarea
          className="ent-input cp-sample"
          rows={3}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setResult(null);
          }}
          placeholder={t('pages.aIGateway.eGWireToAE070331')}
        />
        <button type="button" className="ent-btn primary sm" disabled={!text.trim()} onClick={runTest}>
          {t('pages.aIGateway.test')}
        </button>
        {result && (
          <div className="cp-preview">
            <div className="cp-preview-head">
              {result.violations.length > 0 ? (
                <span className="cp-count hit">{t('pages.aIGateway.detected', { list: result.violations.join(', ') })}</span>
              ) : (
                <span className="cp-count">{t('pages.aIGateway.nothingDetected')}</span>
              )}
            </div>
            <div className="cp-preview-body">{String(result.redacted)}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const DlpTab: React.FC<{
  dlp: DlpSettings;
  apiUrl: string;
  onChange: (d: DlpSettings) => void;
  onPackSettings: (change: Partial<PackSettings>) => void;
}> = ({ dlp, apiUrl, onChange, onPackSettings }) => {
  const { t } = useTranslation();
  const toggleCategory = (id: DlpCategory) => {
    const has = dlp.categories.includes(id);
    onChange({
      ...dlp,
      categories: has ? dlp.categories.filter((c) => c !== id) : [...dlp.categories, id],
    });
  };

  return (
    <div className="gw-dlp">
      <p className="gw-help">
        {t('pages.aIGateway.inspectPrompts')} <strong>{t('pages.aIGateway.before')}</strong> {t('pages.aIGateway.theyLeaveAgenti5ForTheLLM')}
      </p>

      <div className="gw-card">
        <label className="gw-switch-row">
          <span>
            <strong>{t('pages.aIGateway.dataProtection')}</strong>
            <div className="gw-sub">{t('pages.aIGateway.masterSwitchForOutboundInspection')}</div>
          </span>
          <input
            type="checkbox"
            checked={!!dlp.enabled}
            onChange={(e) => onChange({ ...dlp, enabled: e.target.checked ? 1 : 0 })}
          />
        </label>

        <div className="gw-field">
          <label>{t('pages.aIGateway.onDetection')}</label>
          <div className="gw-radios">
            <label className={!dlp.tokenize && dlp.mode === 'redact' ? 'sel' : ''}>
              <input
                type="radio"
                checked={!dlp.tokenize && dlp.mode === 'redact'}
                onChange={() => onChange({ ...dlp, tokenize: 0, mode: 'redact' })}
              />
              {t('pages.aIGateway.redactForward')}
            </label>
            <label className={!!dlp.tokenize ? 'sel' : ''}>
              <input type="radio" checked={!!dlp.tokenize} onChange={() => onChange({ ...dlp, tokenize: 1 })} />
              {t('pages.aIGateway.tokenizeReversible')}
            </label>
            <label className={!dlp.tokenize && dlp.mode === 'block' ? 'sel' : ''}>
              <input
                type="radio"
                checked={!dlp.tokenize && dlp.mode === 'block'}
                onChange={() => onChange({ ...dlp, tokenize: 0, mode: 'block' })}
              />
              {t('pages.aIGateway.blockRequest')}
            </label>
          </div>
          {!!dlp.tokenize && (
            <p className="gw-sub" style={{ marginTop: 8 }}>
              {t('pages.aIGateway.piiIsSwappedForPlaceholdersBefore')}
            </p>
          )}
        </div>

        <div className="gw-field">
          <label>{t('pages.aIGateway.inspectionPoints')}</label>
          <label className="gw-check">
            <input
              type="checkbox"
              checked={!!dlp.inspect_request}
              onChange={(e) => onChange({ ...dlp, inspect_request: e.target.checked ? 1 : 0 })}
            />
            {t('pages.aIGateway.outboundPromptDataGoingToThe')}
          </label>
          <label className="gw-check">
            <input
              type="checkbox"
              checked={!!dlp.inspect_response}
              onChange={(e) => onChange({ ...dlp, inspect_response: e.target.checked ? 1 : 0 })}
            />
            {t('pages.aIGateway.inboundResponseModelReply')}
          </label>
        </div>
      </div>

      <div className="gw-card">
        <strong>{t('pages.aIGateway.detectors')}</strong>
        <div className="gw-sub" style={{ marginBottom: 12 }}>
          {t('pages.aIGateway.chooseWhichCategoriesAgenti5ScansFor')}
        </div>
        <div className="gw-detectors">
          {DLP_CATALOG.map((c) => {
            const on = dlp.categories.includes(c.id);
            return (
              <button
                key={c.id}
                className={`gw-detector ${on ? 'on' : ''}`}
                onClick={() => toggleCategory(c.id)}
                type="button"
              >
                <span className="gw-detector-check">
                  {on && <Icon name="check" size={14} />}
                </span>
                <span>
                  <strong>{t(c.labelKey)}</strong>
                  <div className="gw-sub">{c.hint}</div>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="gw-card">
        <strong>{t('pages.aIGateway.imageDLP')}</strong>
        <div className="gw-sub" style={{ marginBottom: 12 }}>
          {t('pages.aIGateway.screenshotsAndPicturesAreReadWith')}
        </div>
        <label className="gw-check">
          <input
            type="checkbox"
            checked={!!dlp.scan_images}
            onChange={(e) => onChange({ ...dlp, scan_images: e.target.checked ? 1 : 0 })}
          />
          {t('pages.aIGateway.scanImagesForSensitiveData')}
        </label>
        {!!dlp.scan_images && (
          <>
            <label className="gw-check">
              <input
                type="checkbox"
                checked={dlp.scan_qr !== 0}
                onChange={(e) => onChange({ ...dlp, scan_qr: e.target.checked ? 1 : 0 })}
              />
              {t('pages.aIGateway.decodeQRCodesAndBarcodesShared')}
            </label>
            <div className="gw-field" style={{ marginTop: 8 }}>
              <label>{t('pages.aIGateway.whenAnImageContainsSensitiveData')}</label>
              <select
                value={dlp.image_action_on_hit || 'block_request'}
                onChange={(e) => onChange({ ...dlp, image_action_on_hit: e.target.value as 'block_request' | 'redact_image' })}
              >
                <option value="block_request">{t('pages.aIGateway.blockTheWholeRequestInBlock')}</option>
                <option value="redact_image">{t('pages.aIGateway.replaceOnlyThatImageWithA')}</option>
              </select>
              <div className="gw-sub">{t('pages.aIGateway.inRedactModeOnlyTheOffending')}</div>
            </div>
            <div className="gw-field">
              <label>{t('pages.aIGateway.timeBudgetPerRequestMs')}</label>
              <input
                type="number"
                min={250}
                max={10000}
                value={dlp.image_timeout_ms ?? 3000}
                onChange={(e) => onChange({ ...dlp, image_timeout_ms: Number(e.target.value) })}
              />
            </div>
            <div className="gw-field">
              <label>{t('pages.aIGateway.maxImagesInspectedPerRequest')}</label>
              <input
                type="number"
                min={1}
                max={32}
                value={dlp.image_max_per_request ?? 8}
                onChange={(e) => onChange({ ...dlp, image_max_per_request: Number(e.target.value) })}
              />
            </div>
          </>
        )}
      </div>

      <div className="gw-card">
        <strong>{t('pages.aIGateway.endpointProxySitesItMustNot')}</strong>
        <div className="gw-sub" style={{ marginBottom: 12 }}>
          {t('pages.aIGateway.theEndpointAgentDecryptsAITraffic')}
        </div>
        <label className="gw-check">
          <input
            type="checkbox"
            checked={dlp.proxy_passthrough_private !== 0}
            onChange={(e) => onChange({ ...dlp, proxy_passthrough_private: e.target.checked ? 1 : 0 })}
          />
          {t('pages.aIGateway.dontInterceptPrivateAndInternalDestinations')}
        </label>
        <div className="gw-field" style={{ marginTop: 8 }}>
          <label>{t('pages.aIGateway.extraDomainsAndNetworksOnePer')}</label>
          <textarea
            rows={4}
            placeholder={t('pages.aIGateway.corpExampleComWikiExampleOrg')}
            defaultValue={(dlp.proxy_passthrough || []).join('\n')}
            onBlur={(e) =>
              onChange({ ...dlp, proxy_passthrough: e.target.value.split(/[\s,]+/).filter(Boolean) })
            }
          />
          <div className="gw-sub">{t('pages.aIGateway.aDomainAlsoCoversItsSubdomains')}</div>
        </div>
        <label className="gw-check" style={{ marginTop: 8 }}>
          <input
            type="checkbox"
            checked={!!dlp.proxy_allow_pause}
            onChange={(e) => onChange({ ...dlp, proxy_allow_pause: e.target.checked ? 1 : 0 })}
          />
          {t('pages.aIGateway.letUsersPauseTheAgentFromThe')}
        </label>
        {!!dlp.proxy_allow_pause && (
          <div className="gw-field" style={{ marginTop: 6 }}>
            <label>{t('pages.aIGateway.longestPauseMinutes')}</label>
            <input
              type="number"
              min={1}
              max={480}
              value={dlp.proxy_pause_max_minutes ?? 60}
              onChange={(e) => onChange({ ...dlp, proxy_pause_max_minutes: Number(e.target.value) || 60 })}
            />
            <div className="gw-sub">{t('pages.aIGateway.theAgentTurnsProtectionBackOnBy')}</div>
          </div>
        )}
        <label className="gw-check" style={{ marginTop: 8 }}>
          <input
            type="checkbox"
            checked={!!dlp.proxy_upstream_insecure}
            onChange={(e) => onChange({ ...dlp, proxy_upstream_insecure: e.target.checked ? 1 : 0 })}
          />
          {t('pages.aIGateway.labOnlyDontVerifyTheCertificates')}
        </label>
        {!!dlp.proxy_upstream_insecure && (
          <div className="gw-sub" style={{ color: '#c0392b', marginTop: 6 }}>
            {t('pages.aIGateway.warningAnyoneOnTheNetworkPath')}
            <code> ztai-bdr-agent trust-ca</code>{t('pages.aIGateway.orListingTheDestinationAbove')}
          </div>
        )}
      </div>

      <IndustryPacks apiUrl={apiUrl} onPackSettings={onPackSettings} />

      <CustomDetectors
        patterns={dlp.custom_patterns || []}
        onChange={(next) => onChange({ ...dlp, custom_patterns: next })}
      />

      <div className="gw-card">
        <label className="gw-switch-row">
          <span>
            <strong>{t('pages.aIGateway.promptInjectionShield')}</strong>
            <div className="gw-sub">{t('pages.aIGateway.detectJailbreakInjectionAttemptsInThe')}</div>
          </span>
          <input
            type="checkbox"
            checked={!!dlp.injection_enabled}
            onChange={(e) => onChange({ ...dlp, injection_enabled: e.target.checked ? 1 : 0 })}
          />
        </label>
        {!!dlp.injection_enabled && (
          <div className="gw-field">
            <label>{t('pages.aIGateway.onDetection')}</label>
            <div className="gw-radios">
              <label className={!dlp.injection_block ? 'sel' : ''}>
                <input
                  type="radio"
                  checked={!dlp.injection_block}
                  onChange={() => onChange({ ...dlp, injection_block: 0 })}
                />
                {t('pages.aIGateway.flagAudit')}
              </label>
              <label className={!!dlp.injection_block ? 'sel' : ''}>
                <input
                  type="radio"
                  checked={!!dlp.injection_block}
                  onChange={() => onChange({ ...dlp, injection_block: 1 })}
                />
                {t('pages.aIGateway.blockRequest')}
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------- Threats & Trust tab ---------- */
const ThreatsTab: React.FC<{ threats: { events: ThreatEvent[]; agents: AgentRisk[] } }> = ({ threats }) => {
  const { t } = useTranslation();
  const { t: tr } = useTranslation();
  return (
  <>
    <p className="gw-help">
      {tr('pages.aIGateway.behavioralRiskScoredFromEachAgents')} <strong>{tr('pages.aIGateway.adaptiveRiskBasedEnforcement')}</strong>{tr('pages.aIGateway.highRiskAgentsCanBeAuto')}
    </p>

    <div className="gw-meters" style={{ marginBottom: 22 }}>
      {threats.agents.length === 0 ? (
        <div className="ent-empty">
          <span className="icon">
            <Icon name="checkBadge" />
          </span>
          <h4>{tr('pages.aIGateway.noThreatsDetected')}</h4>
          <p>{tr('pages.aIGateway.riskScoresAppearHereAsAgents')}</p>
        </div>
      ) : (
        threats.agents.map((a) => {
          const band = riskBand(a.risk);
          return (
            <div className={`gw-meter ${band}`} key={a.agent_id}>
              <div className="gw-meter-head">
                <div>
                  <strong>{a.agent_id}</strong>
                  <span className="gw-sub"> · {t('pages.aIGateway.eventCount', { count: a.events })}</span>
                </div>
                <span className={`gw-badge ${band === 'over' ? 'block' : band === 'near' ? 'warn' : 'downgrade'}`}>{tr('pages.aIGateway.risk', { v0: a.risk })}</span>
              </div>
              <div className="gw-bar">
                <div className="gw-bar-fill" style={{ width: `${Math.min(a.risk, 100)}%` }} />
              </div>
              <div className="gw-meter-foot">
                <span className="token-row">
                  {Object.entries(a.breakdown).map(([k, n]) => (
                    <CodeToken key={k} muted>
                      {(THREAT_LABEL[k] ? t(THREAT_LABEL[k].labelKey) : k)} ×{n}
                    </CodeToken>
                  ))}
                </span>
              </div>
            </div>
          );
        })
      )}
    </div>

    {threats.events.length > 0 && (
      <div className="ent-table-wrap scroll">
        <table className="ent-table">
          <thead>
            <tr>
              <th style={{ width: 150 }}>{tr('pages.aIGateway.time')}</th>
              <th>{tr('pages.aIGateway.agent')}</th>
              <th style={{ width: 150 }}>{tr('pages.aIGateway.event')}</th>
              <th>{tr('pages.aIGateway.detail')}</th>
            </tr>
          </thead>
          <tbody>
            {threats.events.map((e) => {
              const threat = THREAT_LABEL[e.kind];
              return (
                <tr key={e.id}>
                  <td style={{ color: 'var(--text-2)' }}>{parseServerDate(e.timestamp).toLocaleString()}</td>
                  <td style={{ fontWeight: 500 }}>{e.agent_id}</td>
                  <td>
                    <span className={`gw-badge ${threat?.cls || 'warn'}`}>{threat ? t(threat.labelKey) : e.kind}</span>
                  </td>
                  <td style={{ color: 'var(--text-2)' }}>{e.detail}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </>
);
};

/* ---------- SIEM Integration Tab ---------- */
const SIEMTab: React.FC<{
  config: SIEMConfig;
  eventTypes: SIEMEventType[];
  onSave: (config: SIEMConfig) => Promise<void>;
  apiUrl: string;
}> = ({ config, eventTypes, onSave, apiUrl }) => {
  const { t } = useTranslation();
  const [localConfig, setLocalConfig] = useState<SIEMConfig>(config);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Sync local config when prop changes
  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  const handleToggleEventType = (eventType: string) => {
    const included = localConfig.event_types.includes(eventType);
    if (included) {
      setLocalConfig({
        ...localConfig,
        event_types: localConfig.event_types.filter((e) => e !== eventType),
      });
    } else {
      setLocalConfig({
        ...localConfig,
        event_types: [...localConfig.event_types, eventType],
      });
    }
  };

  const handleToggleExcludeEventType = (eventType: string) => {
    const excluded = localConfig.exclude_event_types.includes(eventType);
    if (excluded) {
      setLocalConfig({
        ...localConfig,
        exclude_event_types: localConfig.exclude_event_types.filter((e) => e !== eventType),
      });
    } else {
      setLocalConfig({
        ...localConfig,
        exclude_event_types: [...localConfig.exclude_event_types, eventType],
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await onSave(localConfig);
      setMessage({ type: 'success', text: t('pages.aIGateway.siemSaved') });
    } catch (err: any) {
      setMessage({ type: 'error', text: `Error: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEvent = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const res = await fetch(`${apiUrl}/admin/siem/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Test failed');
      const data = await res.json();
      setMessage({ type: 'success', text: data.message });
    } catch (err: any) {
      setMessage({ type: 'error', text: `Error: ${err.message}` });
    } finally {
      setTesting(false);
    }
  };

  const enabledHandlers = [
    localConfig.kvp_enabled && localConfig.kvp_endpoint ? 'KVP' : null,
    localConfig.webhook_enabled && localConfig.webhook_endpoint ? 'Webhook' : null,
    localConfig.slack_enabled && localConfig.slack_webhook ? 'Slack' : null,
    localConfig.teams_enabled && localConfig.teams_webhook ? 'MS Teams' : null,
  ].filter(Boolean);

  return (
    <>
      <p className="gw-help">
        {t('pages.aIGateway.configureSIEMIntegrationToSendAudit')}
      </p>

      {message && (
        <div className={`notice ${message.type}`} style={{ marginBottom: 16 }}>
          <Icon name={message.type === 'success' ? 'checkCircle' : 'warning'} /> {message.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ marginBottom: 8 }}>{t('pages.aIGateway.masterEnable')}</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={localConfig.enabled}
              onChange={(e) => setLocalConfig({ ...localConfig, enabled: e.target.checked })}
            />
            <span>{t('pages.aIGateway.sendAuditEventsToConfiguredSIEM')}</span>
          </label>
          {enabledHandlers.length > 0 && (
            <p style={{ marginTop: 8, fontSize: '0.9em', color: 'var(--text-2)' }}>
              {t('pages.aIGateway.activeHandlers', { list: enabledHandlers.join(', ') })}
            </p>
          )}
        </div>
      </div>

      <div
        style={{
          backgroundColor: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
        }}
      >
        <h3 style={{ marginBottom: 12 }}>{t('pages.aIGateway.kvpHandler')}</h3>
        <p style={{ fontSize: '0.9em', color: 'var(--text-2)', marginBottom: 12 }}>
          {t('pages.aIGateway.formatSpaceSeparatedKeyValuePairs')}
        </p>
        <div className="ent-field">
          <label>{t('pages.aIGateway.endpointOrStdout')}</label>
          <input
            className="ent-input"
            type="text"
            value={localConfig.kvp_endpoint || ''}
            onChange={(e) => setLocalConfig({ ...localConfig, kvp_endpoint: e.target.value })}
            placeholder={t('pages.aIGateway.httpSiemExampleComApiEvents')}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}>
          <input
            type="checkbox"
            checked={localConfig.kvp_enabled}
            onChange={(e) => setLocalConfig({ ...localConfig, kvp_enabled: e.target.checked })}
          />
          <span>{t('pages.aIGateway.enableKVPHandler')}</span>
        </label>
      </div>

      <div
        style={{
          backgroundColor: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
        }}
      >
        <h3 style={{ marginBottom: 12 }}>{t('pages.aIGateway.webhookHandler')}</h3>
        <p style={{ fontSize: '0.9em', color: 'var(--text-2)', marginBottom: 12 }}>
          {t('pages.aIGateway.sendEventsAsJSONPOSTRequests')}
        </p>
        <div className="ent-field">
          <label>{t('pages.aIGateway.webhookURL')}</label>
          <input
            className="ent-input"
            type="url"
            value={localConfig.webhook_endpoint || ''}
            onChange={(e) => setLocalConfig({ ...localConfig, webhook_endpoint: e.target.value })}
            placeholder={t('pages.aIGateway.httpsYourWebhookExampleComEvents')}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}>
          <input
            type="checkbox"
            checked={localConfig.webhook_enabled}
            onChange={(e) => setLocalConfig({ ...localConfig, webhook_enabled: e.target.checked })}
          />
          <span>{t('pages.aIGateway.enableWebhookHandler')}</span>
        </label>
      </div>

      <div
        style={{
          backgroundColor: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
        }}
      >
        <h3 style={{ marginBottom: 12 }}>{t('pages.aIGateway.slackHandler')}</h3>
        <p style={{ fontSize: '0.9em', color: 'var(--text-2)', marginBottom: 12 }}>
          {t('pages.aIGateway.sendEventSummariesToASlack')}
        </p>
        <div className="ent-field">
          <label>{t('pages.aIGateway.slackWebhookURL')}</label>
          <input
            className="ent-input"
            type="url"
            value={localConfig.slack_webhook || ''}
            onChange={(e) => setLocalConfig({ ...localConfig, slack_webhook: e.target.value })}
            placeholder={t('pages.aIGateway.httpsHooksSlackComServicesYOUR')}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}>
          <input
            type="checkbox"
            checked={localConfig.slack_enabled}
            onChange={(e) => setLocalConfig({ ...localConfig, slack_enabled: e.target.checked })}
          />
          <span>{t('pages.aIGateway.enableSlackHandler')}</span>
        </label>
      </div>

      <div
        style={{
          backgroundColor: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
        }}
      >
        <h3 style={{ marginBottom: 12 }}>{t('pages.aIGateway.msTeamsHandler')}</h3>
        <p style={{ fontSize: '0.9em', color: 'var(--text-2)', marginBottom: 12 }}>
          {t('pages.aIGateway.sendEventSummariesToMSTeams')}
        </p>
        <div className="ent-field">
          <label>{t('pages.aIGateway.teamsWebhookURL')}</label>
          <input
            className="ent-input"
            type="url"
            value={localConfig.teams_webhook || ''}
            onChange={(e) => setLocalConfig({ ...localConfig, teams_webhook: e.target.value })}
            placeholder={t('pages.aIGateway.httpsOutlookWebhookOfficeComWebhookb2')}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}>
          <input
            type="checkbox"
            checked={localConfig.teams_enabled}
            onChange={(e) => setLocalConfig({ ...localConfig, teams_enabled: e.target.checked })}
          />
          <span>{t('pages.aIGateway.enableMSTeamsHandler')}</span>
        </label>
      </div>

      <div className="gw-event-panel">
        <h3 className="gw-event-title">{t('pages.aIGateway.eventTypes')}</h3>
        <p className="gw-event-summary">
          {localConfig.event_types.length === 0
            ? t('pages.aIGateway.allEventTypesEnabledSendAll')
            : `${localConfig.event_types.length} event type(s) selected`}
        </p>

        <div className="gw-event-section">
          <h4 className="gw-event-heading">{t('pages.aIGateway.includeEvents')}</h4>
          <div className="gw-event-grid">
            {eventTypes.map((et) => (
              <label
                key={et.event_type}
                className="gw-event-option"
              >
                <input
                  type="checkbox"
                  checked={
                    localConfig.event_types.length === 0 ||
                    localConfig.event_types.includes(et.event_type)
                  }
                  onChange={() => handleToggleEventType(et.event_type)}
                  disabled={localConfig.event_types.length === 0}
                  title={localConfig.event_types.length === 0 ? t('pages.aIGateway.allEventsEnabledByDefault') : undefined}
                />
                <div className="gw-event-copy">
                  <div className="gw-event-name">{et.event_type}</div>
                  <div className="gw-event-description">{et.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="gw-event-section">
          <h4 className="gw-event-heading">{t('pages.aIGateway.excludeEvents')}</h4>
          <div className="gw-event-grid">
            {eventTypes.map((et) => (
              <label
                key={et.event_type}
                className="gw-event-option"
              >
                <input
                  type="checkbox"
                  checked={localConfig.exclude_event_types.includes(et.event_type)}
                  onChange={() => handleToggleExcludeEventType(et.event_type)}
                />
                <div className="gw-event-copy">
                  <div className="gw-event-name">{et.event_type}</div>
                  <div className="gw-event-description">{et.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="ent-btn primary"
          onClick={handleSave}
          disabled={saving}
        >
          <Icon name="checkCircle" /> {saving ? t('pages.aIGateway.saving') : t('pages.aIGateway.saveConfiguration')}
        </button>
        <button
          className="ent-btn"
          onClick={handleTestEvent}
          disabled={testing || !localConfig.enabled || enabledHandlers.length === 0}
          title={
            !localConfig.enabled
              ? t('pages.aIGateway.enableSIEMFirst')
              : enabledHandlers.length === 0
              ? t('pages.aIGateway.configureAtLeastOneHandler')
              : t('pages.aIGateway.sendATestEventToAll')
          }
        >
          <Icon name="bolt" /> {testing ? t('pages.aIGateway.testing') : t('pages.aIGateway.sendTestEvent')}
        </button>
      </div>
    </>
  );
};

/* ---------- Group edit form ---------- */
const GroupForm: React.FC<{
  group: GatewayGroup;
  onChange: (g: GatewayGroup) => void;
  providers: ProviderInfo[];
  models: ModelInfo[];
  credentials: ProviderCredential[];
}> = ({ group, onChange, providers, models, credentials }) => {
  const { t } = useTranslation();
  const set = (patch: Partial<GatewayGroup>) => onChange({ ...group, ...patch });
  const enabledProviders = providers.filter((p) => p.enabled);
  const groupCreds = credentials.filter((c) => c.provider === group.provider && c.enabled);

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <div className="ent-field">
        <label>{t('pages.aIGateway.displayName')}</label>
        <input
          className="ent-input"
          value={group.display_name}
          onChange={(e) => set({ display_name: e.target.value })}
        />
      </div>

      <div className="gw-form-row">
        <div className="ent-field">
          <label>{t('pages.aIGateway.primaryModel2')}</label>
          <ModelSelect
            value={group.primary_model}
            onChange={(v) => set({ primary_model: v })}
            providers={providers}
            models={models}
          />
        </div>
        <div className="ent-field">
          <label>{t('pages.aIGateway.economyModelAutoSwitch')}</label>
          <ModelSelect
            value={group.downgrade_model}
            onChange={(v) => set({ downgrade_model: v })}
            providers={providers}
            models={models}
          />
        </div>
      </div>

      <div className="gw-form-row">
        <div className="ent-field">
          <label>{t('pages.aIGateway.provider')}</label>
          <Select
            value={group.provider || ''}
            onValueChange={(v) => set({ provider: v || null, credential_id: null })}
            placeholder={t('pages.aIGateway.defaultEnv')}
          >
            <SelectItem value="">{t('pages.aIGateway.defaultEnv')}</SelectItem>
            {enabledProviders.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.label}
              </SelectItem>
            ))}
          </Select>
        </div>
        <div className="ent-field">
          <label>{t('pages.aIGateway.apiKey')}</label>
          <Select
            value={group.credential_id || ''}
            onValueChange={(v) => set({ credential_id: v || null })}
            disabled={!group.provider}
            placeholder={group.provider ? t('pages.aIGateway.providerDefaultKey') : '—'}
          >
            {groupCreds.map((c) => (
              <SelectItem key={c.credential_id} value={c.credential_id}>
                {c.label}{c.department ? ` · ${c.department}` : ''} ({c.key_masked || t('pages.aIGateway.noKey')}){c.is_default ? ` • ${t('pages.aIGateway.defaultTag')}` : ''}
              </SelectItem>
            ))}
          </Select>
        </div>
      </div>

      <div className="gw-form-row">
        <div className="ent-field">
          <label>{t('pages.aIGateway.monthlyTokenBudget')}</label>
          <input
            className="ent-input"
            type="number"
            min={0}
            value={group.budget_tokens}
            onChange={(e) => set({ budget_tokens: parseInt(e.target.value || '0', 10) })}
          />
        </div>
        <div className="ent-field">
          <label>{t('pages.aIGateway.budgetInformational')}</label>
          <input
            className="ent-input"
            type="number"
            min={0}
            step={0.5}
            value={group.budget_usd}
            onChange={(e) => set({ budget_usd: parseFloat(e.target.value || '0') })}
          />
        </div>
      </div>

      <div className="gw-form-row">
        <div className="ent-field">
          <label>{t('pages.aIGateway.onBudgetBreach')}</label>
          <Select
            value={group.breach_action}
            onValueChange={(v) => set({ breach_action: v as BreachAction })}
          >
            <SelectItem value="warn">{t('pages.aIGateway.warnOnly')}</SelectItem>
            <SelectItem value="downgrade">{t('pages.aIGateway.autoSwitchToEconomyModel')}</SelectItem>
            <SelectItem value="block">{t('pages.aIGateway.blockRequests')}</SelectItem>
          </Select>
        </div>
        <div className="ent-field">
          <label>{t('pages.aIGateway.warnAtOfBudget')}</label>
          <input
            className="ent-input"
            type="number"
            min={0}
            max={100}
            value={group.warn_threshold_pct}
            onChange={(e) => set({ warn_threshold_pct: parseInt(e.target.value || '0', 10) })}
          />
        </div>
      </div>

      <label className="gw-switch-row">
        <span>
          <strong>{t('pages.aIGateway.enforceThisPolicy')}</strong>
          <div className="gw-sub">{t('pages.aIGateway.whenOffRequestsPassThroughWith')}</div>
        </span>
        <input type="checkbox" checked={!!group.enabled} onChange={(e) => set({ enabled: e.target.checked ? 1 : 0 })} />
      </label>
    </form>
  );
};

/* ---------- Agent budget form ---------- */
const AgentBudgetForm: React.FC<{
  agent: AgentUsageRow;
  onChange: (a: AgentUsageRow) => void;
  providers: ProviderInfo[];
  models: ModelInfo[];
  discovery?: ModelDiscovery | null;
  onRefreshModels?: () => void;
  refreshingModels?: boolean;
}> = ({ agent, onChange, providers, models, discovery, onRefreshModels, refreshingModels }) => {
  const { t } = useTranslation();
  const set = (patch: Partial<AgentUsageRow>) => onChange({ ...agent, ...patch });

  const groupPrimary = agent.group_primary_model;
  const groupEconomy = agent.group_downgrade_model;

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <p className="gw-help">
        {t('pages.aIGateway.aPersonalBudgetAndModelOverride')}
      </p>
      {onRefreshModels && (
        <div className="gw-sub" style={{ marginBottom: 12 }}>
          Models:{' '}
          {discovery && Object.values(discovery.providers).some((p) => p.source === 'live')
            ? `live from ${Object.entries(discovery.providers).filter(([, p]) => p.source === 'live').map(([id]) => id).join(', ')}`
            : 'built-in list'}
          {models.some((m) => m.status === 'pending' && !m.enabled) &&
            ` · ${models.filter((m) => m.status === 'pending' && !m.enabled).length} new awaiting approval (Providers & Keys)`}
          {' · '}
          <button type="button" style={{ background: 'none', border: 0, padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }} onClick={onRefreshModels} disabled={refreshingModels}>
            {refreshingModels ? 'Refreshing…' : 'Refresh models'}
          </button>
        </div>
      )}

      <div className="gw-card" style={{ marginBottom: 16 }}>
        <strong>{t('pages.aIGateway.modelOverride2')}</strong>
        <div className="gw-sub" style={{ marginBottom: 12 }}>
          {t('pages.aIGateway.pinThisAgentToSpecificModels')}
        </div>
        <div className="gw-form-row">
          <div className="ent-field">
            <label>
              {t('pages.aIGateway.primaryModel')}
              {groupPrimary && <span className="gw-sub">{t('pages.aIGateway.group2', { v0: groupPrimary })}</span>}
            </label>
            <ModelSelect
              value={agent.primary_model || ''}
              onChange={(v) => set({ primary_model: v || null })}
              providers={providers}
              models={models}
              allowEmpty
              emptyLabel={groupPrimary ? `— inherit (${groupPrimary}) —` : '— inherit from group —'}
            />
          </div>
          <div className="ent-field">
            <label>
              {t('pages.aIGateway.economyModel')}
              {groupEconomy && <span className="gw-sub">{t('pages.aIGateway.group2', { v0: groupEconomy })}</span>}
            </label>
            <ModelSelect
              value={agent.downgrade_model || ''}
              onChange={(v) => set({ downgrade_model: v || null })}
              providers={providers}
              models={models}
              allowEmpty
              emptyLabel={groupEconomy ? `— inherit (${groupEconomy}) —` : '— inherit from group —'}
            />
          </div>
        </div>
      </div>

      <div className="gw-card" style={{ marginBottom: 16 }}>
        <label className="gw-switch-row">
          <span>
            <strong>{t('pages.aIGateway.responseCompressionSpxColumnar1')}</strong>
            <div className="gw-sub">
              {t('pages.aIGateway.compressesLargeStructuredToolAPIPayloads')}
            </div>
          </span>
          <input
            type="checkbox"
            checked={!!agent.spx_columnar_enabled}
            onChange={(e) => set({ spx_columnar_enabled: e.target.checked ? 1 : 0 })}
          />
        </label>
      </div>

      <div className="gw-card">
        <label className="gw-switch-row">
          <span>
            <strong>{t('pages.aIGateway.enforcePersonalBudget')}</strong>
            <div className="gw-sub">{t('pages.aIGateway.whenOffThisAgentUsesIts')}</div>
          </span>
          <input
            type="checkbox"
            checked={!!agent.enabled}
            onChange={(e) => set({ enabled: e.target.checked ? 1 : 0 })}
          />
        </label>

        <div className="gw-form-row" style={{ marginTop: 12 }}>
          <div className="ent-field">
            <label>{t('pages.aIGateway.monthlyTokenBudget')}</label>
            <input
              className="ent-input"
              type="number"
              min={0}
              value={agent.budget_tokens}
              onChange={(e) => set({ budget_tokens: parseInt(e.target.value || '0', 10) })}
            />
          </div>
          <div className="ent-field">
            <label>{t('pages.aIGateway.budgetInformational')}</label>
            <input
              className="ent-input"
              type="number"
              min={0}
              step={0.5}
              value={agent.budget_usd}
              onChange={(e) => set({ budget_usd: parseFloat(e.target.value || '0') })}
            />
          </div>
        </div>

        <div className="ent-field">
          <label>{t('pages.aIGateway.onBudgetBreach')}</label>
          <Select
            value={agent.breach_action || 'downgrade'}
            onValueChange={(v) => set({ breach_action: v as BreachAction })}
          >
            <SelectItem value="warn">{t('pages.aIGateway.warnOnly')}</SelectItem>
            <SelectItem value="downgrade">{t('pages.aIGateway.autoSwitchToEconomyModel')}</SelectItem>
            <SelectItem value="block">{t('pages.aIGateway.blockRequests')}</SelectItem>
          </Select>
        </div>
      </div>

      <p className="gw-sub" style={{ marginTop: 8 }}>
        {t('pages.aIGateway.thisPeriod')} <strong>{fmt(agent.tokens)}</strong> {t('pages.aIGateway.tokensWord')} · {t('pages.aIGateway.acrossRequests', { cost: usd(agent.cost_usd), requests: fmt(agent.requests) })}
      </p>
    </form>
  );
};

export default AIGateway;
